import { useTranslation } from 'react-i18next';
import { Eyebrow, Input } from '../../../ui/index.js';
import { useRequestsStore } from '../store.js';
import { Card, Field } from './DraftEditorPrimitives.js';
import { DeliveryAddressField } from './DeliveryAddressField.js';
import { DestinationsField } from './DestinationsField.js';
import { DraftRoadsPackagingFields } from './DraftRoadsPackagingFields.js';
import { PickupAddressField } from './PickupAddressField.js';
import { TransportTypePicker } from './TransportTypePicker.js';
import type { DraftScratchpad } from '../types.js';

export function DraftBasicsCard({ d }: { d: DraftScratchpad }) {
  const { t } = useTranslation(['requests']);
  const patchHeader = useRequestsStore((s) => s.patchDraftHeader);
  // WHY (#1057, #2584): non-container drafts use destinations. CONTAINER
  //   owns required pickup + delivery addresses, while SIMPLE additionally
  //   owns an optional pickup address.
  const isContainer = d.kind === 'CONTAINER';
  const isSimple = d.kind === 'SIMPLE';
  // WHY (#1306/#1448): SIMPLE and STANDARD drafts fold the Roads/Packaging
  //   multiselects into this card because their forms drop the separate
  //   dictionary-selections card.
  // WHY (#1393): CONTAINER follows the SIMPLE-like layout — transport
  //   type + pickup/delivery addresses become basics fields.
  // WHY (#1446): the redesigned CONTAINER form drops the Roads/Packaging
  //   selectors entirely (hidden legacy data is preserved by omission on
  //   save).
  // WHY (#1448): the redesigned STANDARD form drops the separate
  //   Roads/Packaging card too, so STANDARD joins SIMPLE in inlining the
  //   selectors here. Only CONTAINER hides them — `isSimple` is no longer
  //   the discriminator; every non-CONTAINER kind inlines them.
  const inlineRoadsPackaging = !isContainer;
  return (
    <Card>
      <Eyebrow>{t('requests:draft.sections.basics')}</Eyebrow>
      <div style={{ height: 12 }} />
      <Field label={t('requests:draft.titleLabel')} required>
        <Input
          value={d.name}
          onChange={(v) => patchHeader({ name: v })}
          placeholder={t('requests:draft.titlePlaceholder')}
        />
      </Field>
      <div style={{ height: 12 }} />
      {isContainer ? (
        <>
          {/* WHY (#1393, Resolved Decision): transport type is a CONTAINER
           * basics field — it moved here from the top of DraftEditor. */}
          <TransportTypePicker
            value={d.transportTypeId}
            onChange={(id) => patchHeader({ transportTypeId: id })}
          />
          <div style={{ height: 12 }} />
          <Field label={t('requests:draft.pickupAddress.label')} required>
            <PickupAddressField d={d} />
          </Field>
          <div style={{ height: 12 }} />
          <Field label={t('requests:draft.deliveryAddress.label')} required>
            <DeliveryAddressField d={d} />
          </Field>
        </>
      ) : (
        <>
          {/* WHY (#700): the freeform from/to inputs are replaced by the
           * destinations multiselect. `routeFrom` is server-hardcoded; the
           * multiselect is the canonical arrival-city source. */}
          <Field label={t('requests:draft.destinations.label')} required>
            <DestinationsField d={d} />
          </Field>
          {isSimple && (
            <>
              <div style={{ height: 12 }} />
              <Field label={t('requests:draft.pickupAddress.label')}>
                <PickupAddressField d={d} />
              </Field>
            </>
          )}
        </>
      )}
      {inlineRoadsPackaging && (
        <>
          <div style={{ height: 12 }} />
          <DraftRoadsPackagingFields d={d} />
        </>
      )}
    </Card>
  );
}
